import {
  ListContentIsland,
  SkeletonListItem,
  ErrorListItem,
  RequisiteListItem,
  Tooltip,
  IconButton,
  PlusIcon,
  CircularProgress,
  CopyButton,
  ScanQRIcon,
  Tabs,
  Tab,
  Section,
  ListItem,
  ListItemIcon,
  CheckFilledIcon,
  ListItemText,
} from '@razz-team/ui-web';
import {
  useAccountCreateCryptoAddressMutation,
  useAccountAddressesQuery,
  useAccountQuery,
} from '../../api/accounts';
import { useTranslation } from 'react-i18next';
import { ApiAccountAddress, ApiAccountAddressFormat } from '../../openapi';
import { useTransfersExternalIncomingNetworksCryptoQuery } from '../../api/transfers';
import { formatIbanForDisplay, isFiatCurrency, mapNetworkToFullName } from '@razz-team/utils';
import { memo, useCallback, useMemo, useState } from 'react';
import { useIdempotencyKey } from '../../hooks/useIdempotencyKey';
import { QRDialog } from './QRDialog';
import { useBoolean } from 'usehooks-ts';

function mapNetworkToType(network: string) {
  switch (network) {
    case 'SEPA':
      return 'Local';
    case 'SWIFT':
      return 'International';
    default:
      return 'International';
  }
}

function Loader() {
  return (
    <>
      <SkeletonListItem />
      <SkeletonListItem />
    </>
  );
}

const AccountAddress = memo(function AccountAddress({ address }: { address: ApiAccountAddress }) {
  const { t } = useTranslation();
  const dialogOpened = useBoolean(false);

  if (!address) {
    return null;
  }

  if (address.value) {
    if (address.designation === 'VOID') {
      return (
        <RequisiteListItem
          title={mapNetworkToFullName(address.type)}
          value={undefined}
          error={t('Deposit address no longer valid')}
        />
      );
    }

    return (
      <>
        <RequisiteListItem
          title={mapNetworkToFullName(address.type)}
          value={address.value}
          actions={
            <Tooltip title={t('Scan QR')}>
              <IconButton onClick={dialogOpened.setTrue}>
                <ScanQRIcon color="info" />
              </IconButton>
            </Tooltip>
          }
        />
        <QRDialog open={dialogOpened.value} onClose={dialogOpened.setFalse} address={address} />
      </>
    );
  }

  if (address.designation === 'VOID') {
    return <ErrorListItem error={t('Deposit address no longer valid')} />;
  }

  return (
    <>
      {address.supportedNetworks?.includes('SEPA_INSTANT') ? (
        <ListItem sx={{ gap: 2 }}>
          <ListItemIcon>
            <CheckFilledIcon color="info" />
          </ListItemIcon>
          <ListItemText>{t('Supports SEPA Instant')}</ListItemText>
        </ListItem>
      ) : null}
      {address.supportedNetworks?.includes('SWIFT') ? (
        <ListItem sx={{ gap: 2 }}>
          <ListItemIcon>
            <CheckFilledIcon color="info" />
          </ListItemIcon>
          <ListItemText>{t('Supports SWIFT')}</ListItemText>
        </ListItem>
      ) : null}
      {address.referenceProvider ? (
        <RequisiteListItem title={t('Recipient name')} value={address.referenceProvider} />
      ) : null}
      {address.referenceProviderAddress ? (
        <RequisiteListItem
          title={t('Recipient address')}
          value={address.referenceProviderAddress}
        />
      ) : null}
      {address.routingNumber ? (
        <RequisiteListItem title={t('BIC / SWIFT code')} value={address.routingNumber.value} />
      ) : null}
      {address.accountNumber ? (
        <RequisiteListItem
          title={t('IBAN')}
          value={formatIbanForDisplay(address.accountNumber.value)}
          valueToCopy={address.accountNumber.value}
        />
      ) : null}
      {address.reference ? (
        <RequisiteListItem title={t('Reference')} value={address.reference} />
      ) : null}
    </>
  );
});

function RequisitesTabs({
  addresses,
  onChange,
  value,
}: {
  addresses: ApiAccountAddress[];
  onChange: (value: number) => void;
  value: number;
}) {
  const { t } = useTranslation();

  // If it is a fiat address
  if (!addresses[0]?.value) {
    return (
      <>
        {addresses.length > 1 ? (
          <Section>
            <Tabs value={value} onChange={(_: unknown, nextType: number) => onChange(nextType)}>
              {addresses.map((address, index) => (
                <Tab
                  label={t(mapNetworkToType(address.supportedNetworks?.[0] ?? ''))}
                  value={index}
                  key={index}
                />
              ))}
            </Tabs>
          </Section>
        ) : null}
        <AccountAddress address={addresses[value]} />
      </>
    );
  } else {
    return addresses.map((a) => <AccountAddress address={a} key={a.id} />);
  }
}

function CryptoRequisiteListItem({
  accountId,
  network,
}: {
  accountId: string;
  network: ApiAccountAddressFormat;
}) {
  const { t } = useTranslation();
  const idempotencyKey = useIdempotencyKey();
  const { mutate, error, isPending } = useAccountCreateCryptoAddressMutation({
    accountId,
    idempotencyKey,
  });

  const createAddress = useCallback(() => mutate({ type: network.type }), [network.type, mutate]);

  return (
    <RequisiteListItem
      title={mapNetworkToFullName(network.type)}
      value={undefined}
      error={error}
      button={
        isPending ? (
          <CircularProgress size={36} sx={{ padding: 2 }} color="info" />
        ) : (
          <Tooltip title={t('Add network')}>
            <IconButton onClick={createAddress}>
              <PlusIcon sx={{ color: 'text.secondary' }} />
            </IconButton>
          </Tooltip>
        )
      }
    />
  );
}

export function AccountRequisitesIsland({ accountId }: { accountId: string }) {
  const { t } = useTranslation();
  const addressQuery = useAccountAddressesQuery({ id: accountId });
  const accountQuery = useAccountQuery({ accountId: accountId });
  const currency = accountQuery.data?.balance.currency;
  const [selectedAddressToCopy, setSelectedAddressToCopy] = useState<number>(0);
  const networksQuery = useTransfersExternalIncomingNetworksCryptoQuery({
    currency: currency && !isFiatCurrency(currency) ? currency : undefined,
  });

  const availableNetworks = useMemo(() => {
    if (networksQuery.data && addressQuery.data) {
      return networksQuery.data.filter(
        (network) => !addressQuery.data?.addresses.some((address) => address.type === network.type),
      );
    }
  }, [networksQuery.data, addressQuery.data]);

  const noDepositDetails = useMemo(() => {
    if (networksQuery.isSuccess && !networksQuery.data?.length) {
      return t('No available networks for top up');
    }
    if (
      accountQuery.data &&
      currency &&
      isFiatCurrency(currency) &&
      !addressQuery.data?.addresses.length
    ) {
      return t('No available deposit details');
    }
  }, [networksQuery, addressQuery.data, accountQuery.data, currency, t]);

  const copyAllText = useMemo(() => {
    const address = addressQuery.data?.addresses[selectedAddressToCopy];
    if (!address || address.designation === 'VOID') {
      return '';
    }
    const rows = [];
    if (address.value) {
      rows.push(`${address.type}: ${address.value}`);
    } else {
      if (address.referenceProvider) {
        rows.push(`${t('Recipient name')}: ${address.referenceProvider}`);
      }
      if (address.referenceProviderAddress) {
        rows.push(`${t('Recipient address')}: ${address.referenceProviderAddress}`);
      }
      if (address.routingNumber) {
        rows.push(`${t('BIC / SWIFT code')}: ${address.routingNumber.value}`);
      }
      if (address.accountNumber) {
        rows.push(`${t('IBAN')}: ${address.accountNumber.value}`);
      }
      if (address.reference) {
        rows.push(`${t('Reference')}: ${address.reference}`);
      }
    }
    return rows.join('\n');
  }, [addressQuery.data, t, selectedAddressToCopy]);

  return (
    <ListContentIsland
      title={t('Deposit address')}
      actions={
        copyAllText ? (
          <CopyButton value={copyAllText} variant="text">
            {t('Copy all')}
          </CopyButton>
        ) : undefined
      }
    >
      {addressQuery.isPending ? (
        <Loader />
      ) : addressQuery.isError ? (
        <ErrorListItem error={addressQuery.error} />
      ) : noDepositDetails ? (
        <ErrorListItem error={noDepositDetails} />
      ) : (
        <>
          <RequisitesTabs
            addresses={addressQuery.data?.addresses ?? []}
            onChange={setSelectedAddressToCopy}
            value={selectedAddressToCopy}
          />
          {availableNetworks?.map((network) => (
            <CryptoRequisiteListItem key={network.type} network={network} accountId={accountId} />
          ))}
        </>
      )}
    </ListContentIsland>
  );
}
